<template>
  <Card
    padding-class="tw-p-0"
    class="tw-overflow-hidden tw-border tw-border-secondary-100 tw-@container"
  >
    <Disclosure>
      <template #activator="{ toggle, isActive, ariaExpanded, ariaControls }">
        <div
          class="tw-flex tw-flex-col tw-space-y-4 tw-p-4 tw-transition-colors @xl:tw-flex-row @xl:tw-items-center @xl:tw-space-x-5 @xl:tw-space-y-0"
          :class="[
            isActive
              ? 'tw-border-b tw-border-secondary-100 tw-bg-secondary-50'
              : '',
          ]"
        >
          <ContentDeliveryIcon
            class="tw-hidden tw-h-12 tw-w-12 tw-shrink-0 @xl:tw-block"
          />
          <div class="tw-grow">
            <div class="tw-font-semibold tw-text-secondary-500">
              Let AI find the right mentor for you in seconds
            </div>
            <div class="tw-mt-1 tw-text-sm tw-text-secondary-400">
              Tell us what you need, and we’ll instantly match you with the relevant mentors ready to help
            </div>
          </div>
          <div class="tw-shrink-0">
            <IconButton
              v-if="isActive"
              @click="
                () => {
                  toggle()
                  handleToggleDisclosure()
                }
              "
              :aria-expanded="ariaExpanded"
              :aria-controls="ariaControls"
              color="outline-light-purple"
            >
              <BaseIcon name="e-remove-1" />
            </IconButton>
            <BaseButton
              v-else
              @click="
                () => {
                  toggle()
                  handleToggleDisclosure()
                }
              "
              :aria-expanded="ariaExpanded"
              :aria-controls="ariaControls"
              color="outline-light-purple"
            >
              <LightningIcon class="tw-mr-2 tw-h-4 tw-w-4" />
              Get matched
            </BaseButton>
          </div>
        </div>
      </template>
      <template #content>
        <div v-if="matchedMentors.length" class="tw-space-y-4 tw-p-4">
          <div class="tw-text-center tw-text-sm tw-font-semibold tw-uppercase">
            Results
          </div>
          <div class="tw-grid md:tw-grid-cols-3 tw-gap-4">
            <RecommendedMentorCard
              v-for="mentor in matchedMentors"
              :key="mentor.slug"
              :score="explanationAndScore[mentor.slug]?.score ?? 0"
              :mentor="mentor"
              :explanation="explanationAndScore[mentor.slug]?.explanation ?? ''"
            />
          </div>
        </div>
        <form v-else @submit.prevent="handleSubmitForm" class="tw-p-4">
          <FormInput
            v-model="challenge"
            as="textarea"
            rows="4"
            tooltip="Write a couple of sentences describing the core challenge you're facing."
            :min-characters="50"
            :max-characters="2000"
            :is-error="Boolean(errors.challenge)"
            :errors="errors.challenge"
            @input="clearErrors"
            @click="handleInputClick"
            :readonly="!isUserLoggedIn"
          >
            <template #label>What’s your main pain/challenge?</template>
          </FormInput>
          <div
            v-if="isProcessing"
            class="tw-mt-4 tw-text-sm tw-text-neutral-500"
          >
            <LoadingCircle size="16" class="tw-mr-2 tw-inline-block" />
            Matching you with other mentors...
          </div>

          <div class="tw-text-right">
            <BaseButton
              type="submit"
              color="light-purple"
              :is-disabled="isProcessing"
            >
              <LightningIcon class="tw-mr-2 tw-h-4 tw-w-4" />
              Get matches
            </BaseButton>
          </div>
        </form>
      </template>
    </Disclosure>
  </Card>
</template>

<script setup lang="ts">
import Card from '@/shared/components/Card.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import LightningIcon from '@/assets/images/icons/lightning.svg'
import ContentDeliveryIcon from '@/assets/images/colored-icons/content-delivery.svg'
import Disclosure from '@/shared/components/Disclosure.vue'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import IconButton from '@/shared/components/IconButton.vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables.js'
import { usePermissionSystem } from '@/shared/composables/usePermissionSystem.js'
import { useModals } from '@/shared/composables/useModals.js'
import { ref } from 'vue'
import axios from 'axios'
import RecommendedMentorCard from '@/modules/growthmentor/matches-v2/components/RecommendedMentorCard.vue'
import { useMatchingExplanation } from '@/modules/growthmentor/matches-v2/composables/useMatchingExplanation.ts'
import { notify } from '@kyvg/vue3-notification'

const { isUserLoggedIn } = usePermissionSystem()
const { openModal } = useModals()

const challenge = ref('')
const isProcessing = ref(false)
const matchedMentors = ref([])
const explanationAndScore = ref({})

const errors = ref({})
const clearErrors = () => {
  errors.value = {}
}

const handleSubmitForm = async () => {
  if (!isUserLoggedIn.value) {
    return openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
  }

  isProcessing.value = true
  try {
    const { data } = await axios.post('/matches_v2', {
      challenge: challenge.value,
    })
    matchedMentors.value = data.mentors.map((mentor) => ({
      ...mentor.user,
    }))
  } catch (error) {
    if (error.response.data) {
      errors.value = error.response.data.errors
      if (error.response.data.errors.nonFieldError) {
        notify({
          type: 'error',
          text: error.response.data.errors.nonFieldError,
        })
      }
    }
  } finally {
    isProcessing.value = false
  }
}

const handleInputClick = () => {
  if (isUserLoggedIn.value) return
  openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
}

useMatchingExplanation({
  onReceived: (data) => {
    data.mentors.forEach((mentor) => {
      explanationAndScore.value[mentor.slug] = {
        explanation: mentor.explanation,
        score: mentor['percentage-match'],
      }
    })
  },
})

const handleToggleDisclosure = () => {
  challenge.value = ''
  matchedMentors.value = []
  explanationAndScore.value = {}
}
</script>
